import { useEffect, useState } from "react"
import { createUseStyles } from 'react-jss'
import { useAppSelector } from "../../redux/hooks"
import { useAppDispatch } from '../../redux/hooks'
import { EmployeeReadyForDeactivation } from "../../redux/types"
import FullScreenLoadingIndicator from "../../components/FullScreenLoadingIndicator"
import Card from "../../components/Card"
import ConfirmModal from "../../components/ConfirmModal"
import { useTranslation } from "react-i18next"
import Button from "../../components/Button"
import { historyPageIndexChanged, pageIndexChanged } from "../../redux/slices/employeeDeactivationSlice"
import useCompanyGroupEmployeeDeactivationActions from "../../hooks/useCompanyGroupEmployeeDeactivationActions"
import DeactivationList from "../../components/Activation/DeactivationList"
import DeactivationHistoryList from "../../components/Activation/DeactivationHistoryList"
import ImportFileForDeactivation from "../../components/Activation/ImportFileForDeactivation"
import useFileActions from "../../hooks/useFileActions"

const CompanyGroupDeactivations = () => {
    const styles = useStyles()
    const { t } = useTranslation('deactivations');
    const dispatch = useAppDispatch()

    const [selection, setSelection] = useState<EmployeeReadyForDeactivation[]>([])

    const [confirmDeactivationModalVisible, setConfirmDeactivationModalVisible] = useState<boolean>(false)
    const [deactivationCandidates, setDeactivationCandidates] = useState<EmployeeReadyForDeactivation[]>([]) 

    const { deactivateEmployees, getDeactivatedEmployees } = useCompanyGroupEmployeeDeactivationActions()
    const loading = useAppSelector(state => state.employeeDeactivation.loading)
    const pendingEmployees = useAppSelector(state => state.employeeDeactivation.pendingEmployees)
    const pageIndex = useAppSelector(state => state.employeeDeactivation.pageIndex)
    const deactivatedEmployees = useAppSelector(state => state.employeeDeactivation.deactivatedEmployees)
    const historyPageIndex = useAppSelector(state => state.employeeDeactivation.historyPageIndex)
    const pageSize = useAppSelector(state => state.employeeDeactivation.pageSize)
    const filesLoading = useAppSelector(state => state.files.loading)
    const files = useAppSelector(state => state.files.files)

    const { getCompanyGroupFiles } = useFileActions()

    useEffect(() => {
        if (files.length === 0) {
            getCompanyGroupFiles()
        }
    }, [])

    useEffect(() => {
        if (deactivatedEmployees.length === 0) {
            getDeactivatedEmployees()
        }
    }, [])

    const onDeactivateClick = (employee: EmployeeReadyForDeactivation) => {
        setDeactivationCandidates([employee])
        setConfirmDeactivationModalVisible(true)      
    }

    const onDeactivateSelectedClick = () => {
        setDeactivationCandidates([...selection])
        setConfirmDeactivationModalVisible(true)    
    }

    const handleDeactivationConfirmed = () => {
        deactivateEmployees(deactivationCandidates).then(() => getDeactivatedEmployees())
        setDeactivationCandidates([])
        setConfirmDeactivationModalVisible(false)   
        setSelection([])  
    }

    const onPageChange = (pageIndex: number) => {
        dispatch(pageIndexChanged(pageIndex))
    }

    const onSelectionChange = (selection: EmployeeReadyForDeactivation[]) => {
        setSelection([...selection])
    }

    const onHistoryPageChange = (pageIndex: number) => {
        dispatch(historyPageIndexChanged(pageIndex))
    }

    return (
        <>
        <Card className={styles.card}>
            <h2>
                {t('title')}
            </h2>
            <div className={styles.info}>
                {t('subtitle')}
            </div>
        </Card>
         <Card>
            <div className={styles.buttonContainer}>
                <Button 
                    disabled={selection.length === 0}
                    onClick={onDeactivateSelectedClick} 
                    title={t('deactivateSelected')} 
                    className={styles.button}  
                />
            </div>
            {pendingEmployees?.length > 0 &&
            <ImportFileForDeactivation isCompanyGroupFile={true} />
            }
            <DeactivationList 
                employees={pendingEmployees ?? []}
                pageIndex={pageIndex}
                pageSize={pageSize} 
                onDeactivateClick={onDeactivateClick} 
                onPageChange={onPageChange} 
                onSelectionChanged={onSelectionChange}
            />
           <ConfirmModal 
                title={t('deactivateEmployee')}     
                confirmText={t('confirmDeactivation')}
                visible={confirmDeactivationModalVisible}
                onConfirmClick={handleDeactivationConfirmed}
                onCancelClick={() => setConfirmDeactivationModalVisible(false)}
            />
            <FullScreenLoadingIndicator visible={loading || filesLoading} />
        </Card>
        <Card className={styles.historyCard}>
            <h3 className={styles.sectionTitle}>
                {t('historyTitle')}
            </h3>
            {deactivatedEmployees &&
            <DeactivationHistoryList 
                employees={deactivatedEmployees}
                pageIndex={historyPageIndex}
                pageSize={pageSize} 
                onPageChange={onHistoryPageChange} 
            />}
        </Card>
        </>
    )
} 

export default CompanyGroupDeactivations

const useStyles = createUseStyles({
    card: {
        marginBottom: 20
    },
    info: {
        marginTop: 20,
    },
    buttonContainer: {
        display: 'flex',
        flex: 1,
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: 10
    },
    button: {
        alignSelf: 'flex-end'
    },
    sectionTitle: {
        marginBottom: 10
    },
    historyCard: {
        marginTop: 20
    }
})
